import { useCallback, useMemo } from 'react';
import { apiRoute } from './api-route';
import { StatType } from 'shared/@generated/models';
import { createRequestClient } from './api-client';
import { useUserStore } from 'shared/state/user';
import { useLocale } from 'shared/library/i18n/use-locale';

type PendingStats = {
  uuids: Set<string>;
  timeout: ReturnType<typeof setTimeout> | null;
};

const pendingStats: Record<StatType, PendingStats> = {
  [StatType.Impression]: { uuids: new Set(), timeout: null },
  [StatType.Click]: { uuids: new Set(), timeout: null },
};

const BATCH_DELAY_MS = 100;

export function useStatsBatcher() {
  const apiKey = useUserStore((state) => state.apiKey);
  const { languageCode, countryCode } = useLocale();
  const requestClient = useMemo(
    () =>
      createRequestClient({
        languageCode,
        apiKey,
        headers: { 'X-Country': countryCode },
      }),
    [apiKey, countryCode, languageCode],
  );

  const batchIncrementStats = useCallback(
    (uuid: string, statType: StatType) => {
      const pending = pendingStats[statType];

      pending.uuids.add(uuid);

      if (pending.timeout) {
        clearTimeout(pending.timeout);
      }

      pending.timeout = setTimeout(() => {
        if (pending.uuids.size === 0) return;

        const uuids = Array.from(pending.uuids);
        pending.uuids.clear();
        pending.timeout = null;

        requestClient(apiRoute('media-assets.increment-stats'), {
          uuids,
          type: statType,
        }).catch(() => {});
      }, BATCH_DELAY_MS);
    },
    [requestClient],
  );

  return { batchIncrementStats };
}
